import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Worker, Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import fr_FR from '@react-pdf-viewer/locales/lib/fr_FR.json';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import Image from './Image';

const DocumentViewer = ({ fileUrl, title, onViewed, children, onLoadError, onUnsupportedFormat }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const url = new URL(fileUrl);
  const extension = url.pathname.split('.').pop().toLowerCase();

  const isPdf = extension === 'pdf';
  const isImage = ['gif', 'jpeg', 'jpg', 'png'].includes(extension);
  const isDoc = ['docx', 'ppsx', 'ppt', 'pptx', 'rtf', 'xls', 'xlsx', 'xlsxm'].includes(extension);
  const isUnsupported = !isPdf && !isDoc && !isImage;

  const decodedFileUrl = !isDoc
    ? fileUrl
    : `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}&ui=fr-FR`;

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: () => [] // => plus aucun onglet => plus de sidebar
  });

  // Si type non supporté → fallback parent
  useEffect(() => {
    if (isUnsupported) {
      onUnsupportedFormat();
    }
  }, [isUnsupported, onUnsupportedFormat]);

  // Si erreur → notifier le parent
  useEffect(() => {
    if (hasError) {
      onLoadError();
    }
  }, [hasError, onLoadError]);

  // Si chargé → notifier le parent
  useEffect(() => {
    if (isLoaded) {
      onViewed();
    }
  }, [isLoaded, onViewed]);

  const handleDocumentError = useCallback(() => {
    setHasError(true);
  }, [fileUrl]);

  const handleDocumentLoad = useCallback(() => {
    setIsLoaded(true);
  }, [fileUrl]);

  if (isUnsupported || hasError) {
    return children;
  }

  if (isImage) {
    return <Image alt={title} src={fileUrl} onLoadedFallback={handleDocumentLoad} onError={handleDocumentError} />;
  }

  if (!isPdf && isDoc) {
    return (
      <iframe
        src={decodedFileUrl}
        title={title}
        width="100%"
        height="100%"
        style={{ border: 0 }}
        onError={handleDocumentError}
        onLoad={handleDocumentLoad}
      />
    );
  }

  // PDF - Utiliser Worker avec chemin dynamique basé sur PUBLIC_URL
  // Fallback sur CDN si le fichier local n'est pas accessible
  const baseUrl = window.env.PUBLIC_URL || '';
  const workerUrl = `${baseUrl}/pdf.worker.min.js`;
  
  // PDF - Utiliser Worker avec le fichier worker local (version correspondante à pdfjs-dist)
  return (
    <Worker workerUrl={workerUrl}>
      <Viewer
        localization={fr_FR}
        fileUrl={decodedFileUrl}
        defaultScale={SpecialZoomLevel.PageFit}
        viewMode="SinglePage"
        scrollMode="Vertical"
        plugins={[defaultLayoutPluginInstance]}
        onDocumentLoad={handleDocumentLoad}
        renderError={() => {
          handleDocumentError();
          return null; // on ne rend rien ici
        }}
      />
    </Worker>
  );
};

DocumentViewer.propTypes = {
  fileUrl: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  onViewed: PropTypes.func,
  children: PropTypes.node,
  onLoadError: PropTypes.func,
  onUnsupportedFormat: PropTypes.func
};

export default DocumentViewer;
